import { useCallback, useMemo, useState } from 'react';
import {
  RelatedDocumentsDto,
  TableConfigDto,
  TableRowDto,
} from '@asset-metrix/portal-gateway-client-js';
import { useDisclosure } from '@mantine/hooks';

export type TableSidebarRowData = {
  rowId: string;
  data: TableRowDto['data'];
};

export type TableSidebarProps = {
  data: TableSidebarRowData | null;
  opened: boolean;
  onClose: () => void;
  relatedDocumentsConfig?: RelatedDocumentsDto;
};

type UseTableSidebarPropertiesReturnValue = {
  props?: TableSidebarProps;
  toggle?: (data: TableSidebarRowData) => void;
};

export function useTableSidebarProperties(
  config: TableConfigDto
): UseTableSidebarPropertiesReturnValue {
  const [isOpen, { open: openSidebar, close: closeSidebar }] =
    useDisclosure(false);
  const [state, setState] = useState<TableSidebarRowData | null>(null);

  const hasSidebar = useMemo(
    () => config.relatedDocuments != null,
    [config.relatedDocuments]
  );

  const props = useMemo<TableSidebarProps | undefined>(() => {
    if (!hasSidebar) {
      return undefined;
    }

    const resetStateAndClose = () => {
      closeSidebar();
      setState(null);
    };

    return {
      data: state,
      opened: isOpen,
      onClose: resetStateAndClose,
      relatedDocumentsConfig: config.relatedDocuments,
    };
  }, [hasSidebar, state, isOpen, config.relatedDocuments, closeSidebar]);

  const toggleSidebarCb = useCallback(
    (rowData: TableSidebarRowData) => {
      if (isOpen && state?.rowId === rowData.rowId) {
        closeSidebar();
        setState(null);
      } else {
        setState(rowData);
        openSidebar();
      }
    },
    [state, closeSidebar, isOpen, openSidebar]
  );

  return {
    toggle: hasSidebar ? toggleSidebarCb : undefined,
    props,
  };
}
